import React, {useState} from "react";
import PropTypes from 'prop-types';

import {makeStyles} from "tss-react/mui";

import {
    Box,
    Button,
    Dialog,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Typography
} from "@mui/material";

import {useTranslation} from "react-i18next";

import * as dTokens from "ui-common/components/design-tokens"


// Logitech HD Webcam C310 (046d:081b) ==> Logitech HD Webcam C310
const humanReadableLabel = (label) => {

    let firstBracketIndex = label.indexOf('(');

    if ( firstBracketIndex === -1 ) {
        return label;
    }

    return label.substring(0, firstBracketIndex)
}


// UX requirements.  If there are more than 3 desktop cameras
// display on two rows.
const subdivideArray = (cameraInfoArray) => {
    if (cameraInfoArray.length < 4 ) {
        return [ cameraInfoArray ];
    }

    let modulus = cameraInfoArray.length % 2;
    let size = cameraInfoArray.length / 2;

    return [
        cameraInfoArray.slice(0,size + modulus),
        cameraInfoArray.slice(size + modulus, size + size + modulus),
    ]

}

const dialogStyles = makeStyles()((theme) => ({

    container: {
        position: "relative",
        textAlign: "center",
    },

    centeredText: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%);",
        backgroundColor: "rgba(255,255,255,0.89)",
        paddingLeft: "10px",
        paddingRight: "10px",
        borderRadius: "5px"
    },

    selectControl: {
        width: "100%"
    },

    selectBox: {
        width: "350px",
        minWidth: "350px",
        maxWidth: "350px"
    },

    screenCapBox: {
        position: "relative",
        border: `1px solid ${dTokens.res_grey_300}`,
        borderRadius: "8px",
        width: "224px",
        height: "203px",
        paddingTop: "11px",
        paddingLeft: "11px",
        paddingRight: "11px",
    },

    screenCapBoxSelected: {
        border: `2px solid ${dTokens.res_brand_color_005}`,
        paddingTop: "10px",
        paddingLeft: "10px",
        paddingRight: "10px",
    },

    screenCapBoxNotSelected: {
        "&:hover": {
            border: `1px solid #00497A`
        },
    },

    checkMarkBox: {
        position: "absolute",
        top: 0,
        left: 0,
    },

    cameraImage: {
        width: "200px",
        height: "150px",
        marginBottom: "4px",
        borderRadius: "4px",
        display: "block",
        objectFit: "cover",
    },

    cameraNameSelected: {
        padding: "0 0 10px 0"
    },

    cameraNameNotSelected: {
        padding: "0 0 11px 0"
    },

    buttonContainer: {
        margin: "32px 40px 32px 40px"
    }

}));

const CameraRow =({cameraArray, onKeyUp, setCameraId, cameraId }) => {

    const grid_height =  203;
    const grid_padding = 16;

    let grid_width = cameraArray.length * 224;
    let marginRight = "40px";

    if (cameraArray.length > 1 ) {
        grid_width += ( ( cameraArray.length - 1) * grid_padding);
    }

    const {classes: dialogClasses} = dialogStyles();

    return (
        <Box >
            <Box ml="40px" mr={marginRight} sx={{ width: grid_width, height: grid_height, overflowY: 'visible' }}  >
                <ImageList cols={cameraArray.length} sx={{gap: "16px !important"}} >
                    {cameraArray.map((item, key) => (
                        <ImageListItem key={key} >
                            <Box tabIndex="0"
                                 onClick={ () => { setCameraId(item.deviceId); } }
                                 onKeyUp={(e) => { onKeyUp(e, item.deviceId); } }
                                 className={
                                     cameraId === item.deviceId ? `${dialogClasses.screenCapBoxSelected} ${dialogClasses.screenCapBox} ` :
                                         `${dialogClasses.screenCapBox} ${dialogClasses.screenCapBoxNotSelected}`
                                 }>
                                <img
                                    src={item.image}
                                    srcSet={item.image}
                                    alt={item.label}
                                    className={dialogClasses.cameraImage}
                                />


                                <ImageListItemBar 
                                    classes={{titleWrap: cameraId === item.deviceId ? dialogClasses.cameraNameSelected : dialogClasses.cameraNameNotSelected}}
                                    position="below" 
                                    title={humanReadableLabel(item.label)} />
                            </Box>

                        </ImageListItem>
                    ))}
                </ImageList>
            </Box>
        </Box>
    )
};
CameraRow.propTypes = {
    cameraArray: PropTypes.array.isRequired,
    onKeyUp: PropTypes.func.isRequired,
    setCameraId: PropTypes.func.isRequired,
    cameraId: PropTypes.string
}

const ChooseCameraDialog = ( {isOpen, cameraId, cameraInfoArray, doneCallback} ) => {

    const [cameraIdInternal, setCameraIdInternal] = useState(cameraId);

    const {classes: dialogClasses} = dialogStyles();

    const { t } = useTranslation("components.mugshot.FaceMeasurer.ChooseCameraDialog");

    // Localization strings
    const chooseCameraPrompt = t("chooseCameraPrompt");
    const doneButtonText = t("doneButtonText");


    if ( cameraId !== null && cameraIdInternal === null ) {
        setCameraIdInternal(cameraId);
    }

    let subdividedArray = subdivideArray(cameraInfoArray)

    const onKeyUp = (event, deviceId) => {
        const enterKeyCode = 13;
        if (event.keyCode === enterKeyCode) {
            setCameraIdInternal(deviceId);
        }
    };


    return (
        <Dialog open={isOpen} maxWidth="750px">
            <Box ml="40px" mt="32px" mb="16px"  minWidth="390px" >
                <Typography variant="h5" sx={{color: dTokens.res_grey_900}} >{ chooseCameraPrompt }</Typography>
            </Box>

            {subdividedArray.map((item, key) => (
                <CameraRow cameraArray={item} onKeyUp={onKeyUp} setCameraId={setCameraIdInternal}
                           cameraId={cameraIdInternal}
                           key={key}/>
            ))}

            <Box className={dialogClasses.buttonContainer}>
                <Box >
                    <Button
                        disableRipple
                        onClick={() => { doneCallback(cameraIdInternal); }}
                        variant="contained"
                        sx={{fontSize: "13px", height: "40px"}}>
                        { doneButtonText }
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};
ChooseCameraDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    cameraId: PropTypes.string,
    cameraInfoArray: PropTypes.array.isRequired,
    doneCallback: PropTypes.func.isRequired

}

export default ChooseCameraDialog;